import React from 'react';
import {
  ArrowRight,
  Star,
  Edit,
  Trash2,
  MoreVertical,
  Lightbulb,
  ExternalLink,
  BarChart3,
  Users,
  Code2,
  FileText,
  Megaphone,
} from 'lucide-react';

interface FunnelCardProps {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  priority: 'High' | 'Medium' | 'Low';
  tags: string[];
  isFavorite?: boolean;
  currentStage: 'idea' | 'project' | 'task';
  nextStage?: 'project' | 'task' | null;
  onPromote?: (id: string, fromStage: string, toStage: string) => void;
  onToggleFavorite?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  createdAt?: string;
  // New props for project status
  hasProject?: boolean;
  projectId?: string | null;
  projectName?: string | null;
  onGoToProject?: (id: string, projectId: string) => void;
  onDeleteIdeaOnly?: (id: string) => void;
  onDeleteIdeaAndProjects?: (id: string) => void;
  onDeleteEverything?: (id: string) => void;
}

// Category → icon + color, purely presentational (mirrors the same
// keyword-heuristic pattern used for agent icon coloring elsewhere in the
// app) — gives each card visual variety like the reference design without
// needing a stored color field on Idea itself.
function categoryStyle(category: string) {
  const c = (category || '').toLowerCase();
  if (/research|analy|data|market|finance/.test(c)) return { bg: 'var(--app-primary-soft)', color: 'var(--app-primary-deep)', Icon: BarChart3 };
  if (/hiring|hr|people|team/.test(c)) return { bg: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', Icon: Users };
  if (/dev|code|engineer|tech/.test(c)) return { bg: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', Icon: Code2 };
  if (/marketing|social|content|campaign/.test(c)) return { bg: 'var(--app-surface-3)', color: '#993556', Icon: Megaphone };
  if (/doc|note|meeting|writ/.test(c)) return { bg: 'var(--app-primary-soft)', color: 'var(--app-primary-deep)', Icon: FileText };
  return { bg: 'var(--lyra-color-warning-bg)', color: 'var(--lyra-color-warning)', Icon: Lightbulb };
}

const T = {
  border: 'var(--lyra-color-border)',
  ink: 'var(--lyra-color-text)',
  ink2: 'var(--app-text-2)',
  ink3: 'var(--lyra-color-text-muted)',
  ink4: 'var(--app-text-3)',
  accent: 'var(--lyra-color-primary)',
  accentDark: 'var(--app-primary-deep)',
  green: 'var(--lyra-color-success)',
  greenBg: 'var(--lyra-color-success-bg)',
  greenBd: 'var(--app-success-border)',
};

const FunnelCard: React.FC<FunnelCardProps> = ({
  id,
  title,
  description,
  category,
  status,
  priority,
  isFavorite = false,
  currentStage,
  nextStage,
  onPromote,
  onToggleFavorite,
  onEdit,
  createdAt,
  hasProject = false,
  projectId,
  projectName,
  onGoToProject,
  onDeleteIdeaOnly,
  onDeleteIdeaAndProjects,
  onDeleteEverything,
}) => {
  const [showDropdown, setShowDropdown] = React.useState(false);

  const getPriorityStyle = (p: string) => {
    switch (p) {
      case 'High': return { bg: 'var(--lyra-color-danger-bg)', color: 'var(--lyra-color-danger)' };
      case 'Medium': return { bg: 'var(--lyra-color-warning-bg)', color: 'var(--lyra-color-warning)' };
      case 'Low': return { bg: 'var(--lyra-color-success-bg)', color: 'var(--lyra-color-success)' };
      default: return { bg: 'var(--lyra-color-surface-2)', color: T.ink3 };
    }
  };

  const getNextStageText = (next: string) => {
    if (currentStage === 'idea' && next === 'project') {
      return hasProject ? 'Go to Project' : 'Convert to Project';
    }
    switch (next) {
      case 'project': return 'Go to Project';
      case 'task': return 'Break into Tasks';
      default: return 'Promote';
    }
  };

  const handlePromoteClick = () => {
    if (currentStage === 'idea' && nextStage === 'project' && hasProject && projectId && onGoToProject) {
      onGoToProject(id, projectId);
    } else if (onPromote && nextStage) {
      onPromote(id, currentStage, nextStage);
    }
  };

  const { bg: iconBg, color: iconColor, Icon: CategoryIcon } = categoryStyle(category);
  const priorityStyle = getPriorityStyle(priority);
  const isGoToProject = hasProject && currentStage === 'idea' && nextStage === 'project';

  return (
    <div
      style={{ background: 'var(--lyra-color-surface)', borderRadius: 14, border: `1px solid ${T.border}`, transition: 'box-shadow .15s, transform .15s' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,.07)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: iconBg, color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CategoryIcon size={16} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              onClick={() => onToggleFavorite?.(id)}
              title="Favorite"
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, display: 'flex', color: isFavorite ? 'var(--lyra-color-warning)' : T.ink4 }}
            >
              <Star size={14} fill={isFavorite ? 'var(--lyra-color-warning)' : 'none'} />
            </button>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowDropdown(v => !v)}
                title="More options"
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, display: 'flex', color: T.ink4 }}
              >
                <MoreVertical size={14} />
              </button>
              {showDropdown && (
                <>
                  <div onClick={() => setShowDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 11, background: 'var(--lyra-color-surface)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.1)', border: `1px solid ${T.border}`, minWidth: 210, overflow: 'hidden' }}>
                    <button
                      onClick={() => { onEdit?.(id); setShowDropdown(false); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 12.5, color: T.ink2 }}
                    >
                      <Edit size={13} /> Edit
                    </button>
                    <button
                      onClick={() => { onDeleteIdeaOnly?.(id); setShowDropdown(false); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 12.5, color: 'var(--lyra-color-danger)' }}
                    >
                      <Trash2 size={13} /> Delete Idea Only
                    </button>
                    <button
                      onClick={() => { onDeleteIdeaAndProjects?.(id); setShowDropdown(false); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 12.5, color: 'var(--lyra-color-danger)' }}
                    >
                      <Trash2 size={13} /> Delete Idea &amp; Projects
                    </button>
                    <button
                      onClick={() => { onDeleteEverything?.(id); setShowDropdown(false); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 12.5, color: 'var(--lyra-color-danger)', fontWeight: 700 }}
                    >
                      <Trash2 size={13} /> Delete Idea, Projects &amp; Tasks
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: T.ink3, lineHeight: 1.5, marginBottom: 12, minHeight: 36, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {description}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: iconBg, color: iconColor }}>
            {category}
          </span>
          <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: priorityStyle.bg, color: priorityStyle.color }}>
            {priority}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: T.ink4, marginBottom: nextStage ? 12 : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${T.ink4}` }} />
            {status}
          </div>
          {createdAt && <span>{new Date(createdAt).toLocaleDateString()}</span>}
        </div>

        {nextStage && (onPromote || (hasProject && onGoToProject)) && (
          <button
            onClick={handlePromoteClick}
            title={isGoToProject ? `Go to project: ${projectName}` : 'Turn this idea into an actionable project'}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '9px 0', borderRadius: 9, border: 'none', cursor: 'pointer',
              background: isGoToProject ? T.greenBg : T.accent,
              color: isGoToProject ? T.green : 'var(--lyra-color-surface)',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              ...(isGoToProject ? { border: `1px solid ${T.greenBd}` } : {}),
            }}
          >
            {isGoToProject ? <ExternalLink size={13} /> : <ArrowRight size={13} />}
            {getNextStageText(nextStage)}
          </button>
        )}
      </div>
    </div>
  );
};

export default FunnelCard;
